import React from 'react';
import styled from 'styled-components';
import { NotificationContent } from './components/NotificationContent';
import { useTranslation } from 'react-i18next';
import { messages } from './messages';
import { useNavigateToRoute } from 'utils/common';
import { NotificationResponse } from 'api/openapi';
import { Button } from '@progress/kendo-react-buttons';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  setIsAnyNotification?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenNotification: React.Dispatch<React.SetStateAction<boolean>>;
  notificationList: NotificationResponse | undefined;
  onMarkAllAsRead: () => void;
};
export const NotificationDropDownComponent = ({
  isOpen,
  onClose,
  setIsAnyNotification,
  setIsOpenNotification,
  notificationList,
  onMarkAllAsRead,
}: Props) => {
  const redirect = useNavigateToRoute();
  const { t } = useTranslation();
  //api
  const handleMarkAllAsRead = () => {
    if (notificationList?.unreadCount !== 0) {
      onMarkAllAsRead();
    }
  };
  const useClickOutside = (handler: () => void) => {
    const ref = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      setIsAnyNotification?.(false);
      const handleClickOutside = (event: MouseEvent) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          handler();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [handler]);

    return ref;
  };
  const ref = useClickOutside(onClose);
  if (!isOpen) return null;

  return (
    <Wrapper ref={ref}>
      <Header>
        <div className="notification-title">{t(messages.notification())}</div>
        {notificationList?.unreadCount !== 0 && (
          <div className="notification-count">
            {notificationList?.unreadCount}
          </div>
        )}
      </Header>
      <ContentWrapper>
        <div className="button-wrapper">
          <Button
            onClick={handleMarkAllAsRead}
            disabled={notificationList?.unreadCount === 0}
          >
            {t(messages.markAllAsRead())}
          </Button>
        </div>
      </ContentWrapper>
      <Content className="content">
        {notificationList?.notifications?.items?.map(item => (
          <NotificationContent
            data={item}
            setIsOpenNotification={setIsOpenNotification}
          />
        ))}
      </Content>
      <Footer>
        <div
          className="view-all"
          onClick={() => {
            setIsOpenNotification(false);
            redirect('/notification');
          }}
        >
          {t(messages.viewAll())}
        </div>
      </Footer>
    </Wrapper>
  );
};
const Content = styled.div`
  max-height: 448px;
  overflow: auto;
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-track {
    background: ${p => p.theme.layoutPageScrollColor};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    width: 9px;
    background: ${p => p.theme.layoutPageScrollThumbColor};
    border-radius: 10px;
  }
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  .view-all {
    height: 49px;
    font-size: 13px;
    font-weight: 600;
    line-height: 24px;
    text-align: left;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-underline-position: from-font;
    text-decoration-skip-ink: auto;
    cursor: pointer;
    color: ${p => p.theme.viewAllTxtClr};
  }
`;
const ContentWrapper = styled.div`
  border-top: 1px solid ${p => p.theme.contentWrapperBrdrTopClr};
  margin-bottom: 14px;
  .button-wrapper {
    padding: 0 22px;
    display: flex;
    align-items: center;
    justify-content: end;
    height: 55px;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
`;
const Wrapper = styled.div`
  position: absolute;
  top: 52px;
  right: 30px;
  width: 443px;
  height: auto;
  max-height: 653px;
  background: ${p => p.theme.mainWrapperBgClr};
  border-radius: 4px;
  z-index: 99;
  box-shadow: 0px 9px 24px 0px ${p => p.theme.mainWrapperBoxShadowClr};
  .notification-title {
    padding: 0 6px 0 16px;
    display: flex;
    align-items: center;
    height: 40px;
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
    text-align: left;
    text-underline-position: from-font;
    color: ${p => p.theme.notificationTitleCLr};
  }
  .notification-count {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    line-height: 15px;
    text-align: center;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    color: ${p => p.theme.notificationCountClr};
    background: ${p => p.theme.notificationCountBgClr};
    height: 16px;
    min-width: 16px;
    width: auto;
    padding: 0 4px;
    border-radius: 3px;
  }
`;
